import React, { Dispatch, useMemo } from "react";
import DelegationForm from "../DelegationForm/DelegationForm";
import { RootReducer } from "../../store/reducers";
import { connect } from "react-redux";
import useFleetManagerContext from "../../context/hooks/useFleetManagerContext";
import parkingProductsActions from "../../store/actions/parkingProducts.actions";
import { ParkingRightsRequestModel } from "../../models/ParkingRightsRequestModel";
import { DelegationFormDetails, DelegeeWithPmcs } from "../DelegationForm/DelegationFormDetails";
import { DelegeeWithProductsModel } from "../../models/delegations/DelegatedParkingProductsModel";
import { createSelector } from "@reduxjs/toolkit";
import { ParkingProductType } from "../../models";
import { useAppSelector } from "../../store";

interface IDelegeeProps extends DispatchProps {
    onClose: () => void;
    refreshHandler: () => void;
    registrationId: string;
}

function mapToDelegeeWithPmcs(delegee: DelegeeWithProductsModel): DelegeeWithPmcs {
    return {
        id: delegee.registrationId,
        name: delegee.name,
        emailAddress: delegee.emailAddress,
        pmcIds: delegee.products.map((p) => p.pmcId),
    };
}

const selectParkingParkingProducts = createSelector(
    (state: RootReducer) => state.parkingProducts.parkingProducts,
    (parkingProducts) => {
        return {
            ...parkingProducts,
            data: parkingProducts.data?.filter(
                (pp) => pp.productType === ParkingProductType.ParkingRights
            ),
        };
    }
);

const selectDelegeeDetails = createSelector(
    (state: RootReducer) => state.parkingProducts.delegeeDetails,
    (delegeeWithProducts) => ({
        ...delegeeWithProducts,
        data: delegeeWithProducts.data && mapToDelegeeWithPmcs(delegeeWithProducts.data),
    })
);

const selectFormDetails = createSelector(
    [
        selectParkingParkingProducts,
        selectDelegeeDetails,
        (s: RootReducer) => {
            const { loading, isBulkImportCompleted, error } = s.parkingProducts;
            return { loading, isBulkImportCompleted, error };
        },
    ],
    (parkingProducts, delegeeDetails, rest): DelegationFormDetails => ({
        parkingProducts,
        delegeeDetails,
        ...rest,
    })
);

const ParkingRightsForm: React.FC<IDelegeeProps> = (props) => {
    const {
        getParkingProducts,
        delegateParkingRights,
        updateDelegation,
        getDelegeeDetails,
        clearDelegeeDetails,
        resetBulkImportProgress,
        getBulkImportStatus,
        disposeError,
    } = props;

    const formDetails = useAppSelector(selectFormDetails);

    const { fleetManagerState } = useFleetManagerContext();
    const isEditMode = fleetManagerState.isEditDelegeeMode;

    return (
        <div>
            <DelegationForm
                onClose={props.onClose}
                refreshHandler={props.refreshHandler}
                getProducts={getParkingProducts}
                inviteDelegee={delegateParkingRights}
                updateDelegation={updateDelegation}
                getDelegeeDetails={getDelegeeDetails}
                clearDelegeeDetails={clearDelegeeDetails}
                resetBulkImportProgress={resetBulkImportProgress}
                getBulkImportStatus={getBulkImportStatus}
                disposeError={disposeError}
                isEditMode={isEditMode}
                translationContextName="parkingRightsForm"
                delegationFormDetails={formDetails}
            />
        </div>
    );
};

const mapDispatchToProps = (dispatch: Dispatch<any>) => ({
    getParkingProducts: (seasonTicketOwnerCrmId: string, language: string) =>
        dispatch(
            parkingProductsActions.getParkingProducts(seasonTicketOwnerCrmId as string, language)
        ),
    delegateParkingRights: (parkingRights: ParkingRightsRequestModel, callback?: () => void) =>
        dispatch(parkingProductsActions.delegateParkingRights(parkingRights, callback)),
    updateDelegation: (parkingRights: ParkingRightsRequestModel, callback?: () => void) =>
        dispatch(parkingProductsActions.updateDelegation(parkingRights, callback)),
    disposeError: () => dispatch(parkingProductsActions.disposeError()),
    getDelegeeDetails: (registrationId: string, language: string) =>
        dispatch(parkingProductsActions.getDelegeeDetails(registrationId, language)),
    clearDelegeeDetails: () => dispatch(parkingProductsActions.clearDelegeeDetails()),
    resetBulkImportProgress: () => dispatch(parkingProductsActions.resetBulkImportProgress()),
    getBulkImportStatus: (seasonTicketOwnerCrmId: string) =>
        dispatch(parkingProductsActions.getBulkImportStatus(seasonTicketOwnerCrmId)),
});

type DispatchProps = ReturnType<typeof mapDispatchToProps>;

export default connect(null, mapDispatchToProps)(ParkingRightsForm);
